/* eslint-disable max-len */
/* eslint-disable operator-linebreak */
/* eslint-disable quote-props */
import { Box, Button, CircularProgress, Typography, useTheme } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CartIcon, NoteIcon, PinIcon, RefreshIcon, UserIcon } from "../../../../../../components/Icons";
import { useDrawer } from "../../../../../../context/DrawerContext";
import {
  EPrescriptionStatus,
  IMappedPrescriptionDetails,
} from "../../../../../../services/prescriptions/prescriptions.interface";
import { IPrescriptionResponse } from "../../../../../../services/prescription/prescription.interface";
import DetailItem from "./DetailItem/DetailItem";
import DetailTitle from "./DetailTitle/DetailTitle";
import PrescriptionStatus from "../PrescriptionStatus/PrescriptionStatus";
import PrescriptionDrawerLayout from "./PrescriptionDrawerLayout/PrescriptionDrawerLayout";
import { usePrescriptionById, useMedicineUrl } from "../../../../../../services/prescriptions/prescriptions.service";
import { Loading } from "../../../../../../components";
import { VETCOVE_URL } from "../../../../../../config/params";
import { usePetCareStore } from "../../../../../../hooks/redux-hooks/pet-care-data/pet-care-data-store";
import { IGeneralPetCareItem } from "../../../../../../services/Standard-of-care/standard-of-care.interface";
import { userActivityLogger } from "../../../../../../utils/mixpanel";
import { MIXPANEL_EVENTS } from "../../../../../../config/mixPanelEvents";
import { useVetCoveRedirect } from "../../../../../../hooks/useVetCoveRedirect";

interface IPrescriptionData {
  name: string;
  petId: string;
  petName: string;
  status: EPrescriptionStatus;
  remainingFills: number | null;
  allowedUse: number | null;
  quantity: string;
  dose: string;
  instructions: string;
  prescribedBy: string;
  prescribedAt: string;
  expirationDate: string;
}

const formatPrescriptionDate = (dateString: string | undefined): string => {
  if (!dateString) return "";
  const timestamp = Number(dateString);
  const date = isNaN(timestamp) ? new Date(dateString) : new Date(timestamp * 1000);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const mapReduxPrescription = (
  prescription: IPrescriptionResponse,
  prescriptionId: string,
  selectedPet?: IGeneralPetCareItem,
): IPrescriptionData => ({
  name: prescription.name,
  petId: prescriptionId,
  petName: prescription.petName || selectedPet?.name || "/",
  status: prescription.state === "Expired" ? EPrescriptionStatus.EXPIRED : EPrescriptionStatus.ACTIVE,
  remainingFills: null,
  allowedUse: null,
  quantity: prescription.quantity,
  dose: prescription.dose || "/",
  instructions: prescription.indications,
  prescribedBy: prescription.vetName,
  prescribedAt: formatPrescriptionDate(prescription.vetDate),
  expirationDate: formatPrescriptionDate(prescription.expiredOn),
});

const mapApiPrescription = (
  prescription: IMappedPrescriptionDetails,
  selectedPet?: IGeneralPetCareItem,
): IPrescriptionData => ({
  name: prescription.name,
  petId: prescription.petId,
  petName: prescription.petName || selectedPet?.name || "/",
  status: prescription.status,
  remainingFills: prescription.remainingFills,
  allowedUse: prescription.allowedUse,
  quantity: String(prescription.quantity),
  dose: prescription.dose || "",
  instructions: prescription.instructions,
  prescribedBy: prescription.prescribedBy,
  prescribedAt: prescription.prescribedAt,
  expirationDate: prescription.expirationDate,
});

const PrescriptionDetailsContainer = () => {
  const theme = useTheme();
  const { closeDrawer } = useDrawer();
  const [searchParams, setSearchParams] = useSearchParams();
  const prescriptionId = searchParams.get("prescriptionId");
  const source = searchParams.get("source") || "shop";

  const { selectedPrescription, clearSelectedPrescription, selectedPet } = usePetCareStore();
  const { executeGetPrescriptionById, data: apiData, loading: isLoadingFromApi } = usePrescriptionById();
  const { executeGetMedicineUrl, url, loading: urlLoading } = useMedicineUrl();
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const { handleVetCoveLink } = useVetCoveRedirect();

  const hasCachedPrescription = selectedPrescription && selectedPrescription.id === prescriptionId;

  useEffect(() => {
    if (prescriptionId && !hasCachedPrescription) {
      executeGetPrescriptionById(prescriptionId);
    }
  }, [prescriptionId, hasCachedPrescription, executeGetPrescriptionById]);

  useEffect(() => {
    if (shouldRedirect && !urlLoading) {
      handleVetCoveLink(url, VETCOVE_URL);
      setShouldRedirect(false);
    }
  }, [shouldRedirect, url, urlLoading, handleVetCoveLink]);

  useEffect(() => {
    if (prescriptionId && (hasCachedPrescription || (apiData?.prescription && !isLoadingFromApi))) {
      const petName = hasCachedPrescription
        ? selectedPrescription.petName || selectedPet?.name
        : apiData?.prescription?.petName || selectedPet?.name;

      userActivityLogger(MIXPANEL_EVENTS.PHARMACY_PRESCRIPTION_VIEWED, {
        prescription_id: prescriptionId,
        pet_name: petName,
        source,
      });
    }
  }, [prescriptionId, hasCachedPrescription, apiData, isLoadingFromApi, selectedPrescription, selectedPet, source]);

  const handleCloseDrawer = useCallback(() => {
    setSearchParams({});
    clearSelectedPrescription();
    closeDrawer();
  }, [setSearchParams, clearSelectedPrescription, closeDrawer]);

  const handleOpenVetcove = useCallback(
    (isRenewal: boolean) => {
      const prescriptionName = hasCachedPrescription ? selectedPrescription.name : apiData?.prescription?.name;
      const petName = hasCachedPrescription
        ? selectedPrescription.petName || selectedPet?.name
        : apiData?.prescription?.petName || selectedPet?.name;

      const eventName = isRenewal
        ? MIXPANEL_EVENTS.PHARMACY_PRESCRIPTION_RENEW_CLICKED
        : MIXPANEL_EVENTS.PHARMACY_PRESCRIPTION_ORDER_CLICKED;

      userActivityLogger(eventName, {
        prescription_id: prescriptionId,
        prescription_name: prescriptionName,
        pet_name: petName,
        source,
      });

      if (hasCachedPrescription && selectedPrescription.productUrl) {
        handleVetCoveLink(selectedPrescription.productUrl);
        return;
      }
      setShouldRedirect(true);
      const itemId = hasCachedPrescription ? selectedPrescription.id : apiData?.prescription?.itemId;
      executeGetMedicineUrl(itemId);
    },
    [hasCachedPrescription, selectedPrescription, apiData, selectedPet, prescriptionId, source, executeGetMedicineUrl, handleVetCoveLink],
  );

  const handleRenewClick = useCallback(() => {
    handleOpenVetcove(true);
  }, [handleOpenVetcove]);

  if (isLoadingFromApi && !hasCachedPrescription) {
    return (
      <Box className="flex justify-center items-center min-h-[200px]">
        <Loading />
      </Box>
    );
  }

  const prescriptionData: IPrescriptionData = hasCachedPrescription
    ? mapReduxPrescription(selectedPrescription, prescriptionId, selectedPet)
    : mapApiPrescription(apiData?.prescription, selectedPet);

  const {
    name,
    petName,
    status,
    remainingFills,
    allowedUse,
    quantity,
    dose,
    instructions,
    prescribedBy,
    prescribedAt,
    expirationDate,
  } = prescriptionData;

  const shouldShowRenewButton = remainingFills === 0 || status === EPrescriptionStatus.EXPIRED;
  const buttonText = shouldShowRenewButton ? "Renew RX" : "Buy Medicine";
  const buttonIcon = shouldShowRenewButton ? (
    <RefreshIcon color={theme.palette.black.darker} />
  ) : (
    <CartIcon width={20} height={20} color={theme.palette.black.darker} />
  );

  const buttons = (
    <>
      <Button
        variant="contained"
        color="primary"
        fullWidth
        disabled={urlLoading}
        onClick={handleRenewClick}
        startIcon={urlLoading ? <CircularProgress size={20} color="inherit" /> : buttonIcon}
        sx={{
          fontWeight: 600,
          "& .MuiButton-startIcon": {
            "& svg path": {
              transition: "stroke 0.2s ease, fill 0.2s ease",
            },
          },
          "&:hover": {
            "& svg path": {
              stroke: shouldShowRenewButton ? "inherit" : "white",
              fill: shouldShowRenewButton ? "white" : "inherit",
            },
          },
        }}
      >
        {buttonText}
      </Button>
      <Button
        variant="outlined"
        fullWidth
        onClick={handleCloseDrawer}
        sx={{
          fontWeight: 600,
          borderColor: "taupe.light",
          "&:hover": {
            borderColor: "black.main",
            backgroundColor: "transparent",
          },
        }}
      >
        Cancel
      </Button>
    </>
  );

  return (
    <PrescriptionDrawerLayout closeDrawer={handleCloseDrawer} buttons={buttons}>
      <Box className="border-b pb-4" sx={{ borderColor: "neutral.light" }}>
        <Box className="flex items-start gap-3">
          <PinIcon width={24} height={24} color={theme.palette.yellow.lighter} />
          <Typography fontSize={16} lineHeight={1} maxWidth={312}>
            {name || "/"}
          </Typography>
        </Box>

        <Typography fontSize={14} color="gray.dark" my={1}>
          Prescription details for {petName || "/"}
        </Typography>

        <Box className="flex gap-1 flex-wrap">
          <PrescriptionStatus status={status} />
          {remainingFills !== null && remainingFills === 0 && (
            <PrescriptionStatus status={EPrescriptionStatus.NO_REFILLS_REMAINING} />
          )}
        </Box>
      </Box>

      <Box className="pt-3 mb-3">
        <DetailTitle
          title="Medication Information"
          icon={<PinIcon width={20} height={20} color={theme.palette.gray.darker} />}
        />

        <Box
          className="flex flex-col gap-2 rounded-lg p-4 border my-4"
          sx={{ backgroundColor: "neutral.lighter", borderColor: "neutral.main" }}
        >
          <DetailItem label="Name" value={name || "/"} maxWidth={312} />
          {dose && <DetailItem label="Dosage" value={dose} maxWidth={312} />}
          <DetailItem label="Allowed Use" value={allowedUse ?? "/"} />
          <DetailItem label="Quantity" value={quantity || "/"} />
          <DetailItem label="Refills remaining" value={remainingFills ?? "/"} />
        </Box>
      </Box>

      <Box className="pt-3">
        <DetailTitle
          title="Instructions"
          icon={<NoteIcon width={20} height={20} color={theme.palette.gray.darker} />}
        />

        <Box
          className="flex flex-col gap-2 rounded-lg p-4 border my-4"
          sx={{ backgroundColor: "neutral.lighter", borderColor: "neutral.main" }}
        >
          <Typography fontSize={14} lineHeight={1}>
            {instructions || "/"}
          </Typography>
        </Box>
      </Box>

      <Box className="pt-3">
        <DetailTitle
          title="Prescriber Information"
          icon={<UserIcon width={20} height={20} color={theme.palette.gray.darker} />}
        />

        <Box
          className="flex flex-col gap-2 rounded-lg p-4 border my-4"
          sx={{ backgroundColor: "neutral.lighter", borderColor: "neutral.main" }}
        >
          <DetailItem label="Prescribed By" value={prescribedBy || "/"} />
          <DetailItem label="Prescribed At" value={prescribedAt || "/"} />
          <DetailItem label="Expiration Date" value={expirationDate || "/"} />
        </Box>
      </Box>
    </PrescriptionDrawerLayout>
  );
};

export default PrescriptionDetailsContainer;
